import React, { useCallback, useMemo } from 'react';
import Icon from '../../components/Icon';
import FileCard from './FileCard';
import { useTransfers, transferStore } from '../../state/transfers';
import { useIdentity } from '../../hooks/useIdentity';
import { useMesh } from '../../hooks/useMesh';
import './QueueRail.css';

/**
 * F11.3 — Desktop transfer queue rail (artboard 19 `.queue-rail`).
 *
 * Right-column sidebar shown only inside `<DesktopBeam>`. Composes:
 *   - title row ("Transfers")
 *   - `.qstat` aggregate (MB/s, active, done counters)
 *   - one `<FileCard>` per transfer involving the current user (either
 *     outgoing — `fromId === user` — or incoming — `toId === user`)
 *   - a footer reminder that bytes ride the WebRTC data channel (DTLS-encrypted)
 *
 * Empty state: when the user has no transfers (a fresh workspace), the
 * FileCard list is replaced by a centered hint pointing them at the dropzone.
 *
 * Pause/resume/cancel routing — we look up the live transfer handle by id
 * from MeshProvider; if the helper isn't present (test harness without a real
 * mesh, or a synthetic late-joiner transfer) the callback is omitted and the
 * FileCard renders its action button disabled (per its existing contract).
 */
const QueueRail = () => {
  const { user } = useIdentity();
  const mesh = useMesh();

  // Filter at the store level — `useTransfers` memoises the resulting array
  // reference so re-renders stay cheap. We accept either direction here so
  // the rail shows incoming files too (an offer accepted from another peer
  // lands in the same rail as outgoing drops).
  const filter = useCallback(
    (t) => t.fromId === user || t.toId === user,
    [user],
  );
  const transfers = useTransfers(filter);

  // Aggregate counters. `speedMBps` only meaningfully ticks while `state ===
  // 'active'`, so we filter before summing — otherwise a paused transfer's
  // stale speed would inflate the readout.
  const { aggregateMBps, activeCount, doneCount } = useMemo(() => {
    let speed = 0;
    let active = 0;
    let done = 0;
    for (const t of transfers) {
      if (t.state === 'active') {
        active += 1;
        speed += Number(t.speedMBps) || 0;
      } else if (t.state === 'done') {
        done += 1;
      }
    }
    return {
      aggregateMBps: speed.toFixed(1),
      activeCount: active,
      doneCount: done,
    };
  }, [transfers]);

  // Pause/resume/cancel via the mesh-level helpers. We pass these down to each
  // FileCard partially applied to the transfer id. MeshProvider takes care of
  // the wire envelope + store update; the FileCard just needs the callback.
  const makeHandlers = useCallback(
    (transferId) => {
      const onPause =
        typeof mesh.pauseTransfer === 'function'
          ? () => mesh.pauseTransfer(transferId)
          : undefined;
      const onResume =
        typeof mesh.resumeTransfer === 'function'
          ? () => mesh.resumeTransfer(transferId)
          : undefined;
      const onCancel =
        typeof mesh.cancelTransfer === 'function'
          ? () => {
              mesh.cancelTransfer(transferId);
              // Pull the cancelled card out of the rail. The transfer store
              // record stays available for any other subscriber (e.g. a chat
              // Bubble that referenced this transferId).
              transferStore.remove(transferId);
            }
          : undefined;
      return { onPause, onResume, onCancel };
    },
    [mesh],
  );

  const isEmpty = transfers.length === 0;

  return (
    <aside
      className="queue-rail"
      aria-label="Transfer queue"
      data-testid="dbeam-queue-rail"
    >
      <h2>
        <Icon
          name="devices"
          size={14}
          stroke="rgba(255,255,255,.7)"
          aria-hidden
        />{' '}
        Transfers
      </h2>

      <div className="qstat" data-testid="queue-rail-qstat">
        <div className="cell">
          <b style={{ color: 'var(--lan)' }} data-testid="qstat-mbps">
            {aggregateMBps}
          </b>
          <span>MB/s</span>
        </div>
        <div className="cell">
          <b data-testid="qstat-active">{activeCount}</b>
          <span>active</span>
        </div>
        <div className="cell">
          <b data-testid="qstat-done">{doneCount}</b>
          <span>done</span>
        </div>
      </div>

      {isEmpty ? (
        <div className="queue-rail__empty" data-testid="queue-rail-empty">
          <div className="queue-rail__empty-title">No transfers yet.</div>
          <div className="queue-rail__empty-sub">
            Drop a file onto a device.
          </div>
        </div>
      ) : (
        <div className="queue-rail__list" data-testid="queue-rail-list">
          {transfers.map((t) => {
            const handlers = makeHandlers(t.id);
            return (
              <FileCard
                key={t.id}
                transfer={t}
                onPause={handlers.onPause}
                onResume={handlers.onResume}
                onCancel={handlers.onCancel}
              />
            );
          })}
        </div>
      )}

      <div className="qr-footer">
        <Icon name="shield" size={12} stroke="var(--lan)" aria-hidden />{' '}
        WebRTC data channels · DTLS-encrypted
      </div>
    </aside>
  );
};

export default QueueRail;
